import { useEffect, useState } from "react";
import { Link } from "easy-page-router/react";
import { useSSRHook } from "ssr-hook";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";

import { useHead } from "../../services/common";
import { Footer, Header } from "../../Layout";
import { Info, Loading, Page, ReloadLine, ShareButton } from "../../components";
import { useLang } from "../../services/lang";
import { api, handleError, useHandleError } from "../../services/api";
import { getLocalId } from "easy-analytics/client";
import { useLocalHistory } from "../../services/localStorage";

import { WhenTable } from "./components/WhenTable";
import { WhenVoting } from "./components/WhenVoting";
import { WhenClient, WhenVotes } from "../../../types";

export default function WhenPage({ id }: { id: string }) {
    const { dict } = useLang();

    useLocalHistory("when", id);

    const [when, error, isLoading, reload] = useSSRHook<WhenClient>(`/api/when?whenId=${encodeURIComponent(id)}`);
    useHandleError(error);

    const [isVoteMode, setIsVoteMode] = useState(false);
    const [vote, setVote] = useState<null | WhenVotes>(null);

    const [message, setMessage] = useState("");

    useEffect(() => {
        if (vote !== null) return;

        api.getWhenVote({ whenId: id, userId: getLocalId() }).then(([vote, error]) => {
            if (error) {
                handleError(error);
                setVote({});
                return;
            } else {
                setVote(vote);
            }
        });
    }, [id, vote]);

    const title = dict.seo.when.getTitle(when?.title);
    const description = dict.seo.when.getDescription(when?.title, when?.description);

    useHead({
        title,
        description,
    });

    function handleChangeVote(message: string) {
        setMessage(message);
        setIsVoteMode(false);
        reload();
        setVote(null);
    }

    return <Page theme={when?.theme} fixed={<>
        <Header title={dict.when} link={`/${dict.urlWhen}`}>
            <Link href={`/${dict.urlWhen}/${dict.urlNew}`}><Icon path={mdiPlus} size={1} />{dict.newWhen}</Link>
            <ShareButton title={title} description={description} />
        </Header>
        <Footer />
        <ReloadLine stopReload={isVoteMode} value={when} error={error} isLoading={isLoading} onReload={reload} />
    </>}>
        <div className={`full-page when`}>
            {isLoading && <Loading />}
            {error && <p className="error">{dict.whensError}</p>}
            {when && <>
                <h1>{when.title}</h1>
                {when.description && <p className="description width-max pre-line">{when.description}</p>}
                <Info where={when.where} />

                {!isVoteMode && <>
                    <button className="center" onClick={() => setIsVoteMode(true)}>
                        {Object.keys(vote || {}).length === 0 ? dict.whenVote : dict.whenChangeVote}
                    </button>
                    <p className="info">{dict.getNumberVoters(Object.keys(when.users).length)}</p>
                    {message && <p className="info">{message}</p>}
                    <WhenTable when={when} />
                </>}

                {isVoteMode && vote === null && <Loading />}
                {isVoteMode && vote && <WhenVoting when={when} vote={vote} onChange={handleChangeVote} />}
            </>}
        </div>
    </Page>;
}
